import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Grid, Typography } from "@mui/material";
import HeroSection from "./HeroSection";
import FilterDrawer from "../../components/filters/FilterDrawer";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import PublicFilter from "../../components/filters/PublicFilter";
import ClearButton from "../../components/filters/ClearButton";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "../../hooks";
import { PostType } from "../../types/types";
import { usePostRequest } from "../../hooks/postRequest";
import { PUBLIC_BASE_URL } from "../../utils/config";
import PostCard from "../../components/post/PostCard";
import AppPagination from "../../components/AppPagination";
import CopyRight from "./CopyRight";
import Loading from "../../components/Loading";
const debounce = require("lodash.debounce");

const Jobs: React.FC = () => {
  const [openDrawer, setDrawer] = useState<boolean>(false);
  const { search } = useLocation();
  const { loading, data: postsData } = useAppSelector(state => state.post);
  const [posts, setPosts] = useState<PostType[]>([]);
  const [lastPage, setLastPage] = useState<number>(1);

  const postRequest = usePostRequest();

  const fetchData = useCallback(async () => {
    try {
      await postRequest({ baseUrl: PUBLIC_BASE_URL, method: 'GET', data: null, id: null, perPage: 6 });
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  }, [postRequest]);

  const debouncedFetchData = useMemo(() => debounce(fetchData, 300), [fetchData]);

  useEffect(() => {
    if (postsData) {
      const { meta, data } = postsData;
      setLastPage(meta?.lastPage || 1);
      setPosts(data);
    }
  }, [postsData]);

  useEffect(() => {
    debouncedFetchData();
  }, [search, debouncedFetchData]);

  const eachPost = useMemo(() => (
    posts.length > 0 &&
    posts.map((post: PostType, index: number) => (
      <Grid item xs={12} sm={12} md={6} key={index}>
        <PostCard index={index} post={post} fetchData={fetchData} />
      </Grid>
    ))
  ), [posts, fetchData]);

  useEffect(()=>{
    setPosts([])
    fetchData()
  },[fetchData])

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "center",
        minHeight: '100vh'
      }}
    >
      { loading ? (
        <Loading />
      ) : (
        <>
          <HeroSection />
          <Box sx={{ width: "100%", px: { xs: 3, md: 10 }, py: 5 }}>
            <Typography sx={{ fontSize: { xs: "20px", md: "24px" }, display: "inline", fontStyle: "italic", fontWeight: 'bold' }}>
              Recommended Jobs
            </Typography>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item md={3} sx={{ display: { xs: "none", md: "block" } }}>
                <PublicFilter isOpenDrawer={openDrawer} />
              </Grid>
              <Grid item xs={12} md={9}>
                <Box sx={{ display: { xs: 'block', md: "none" }, mb: 3 }}>
                  <Button onClick={() => setDrawer(true)} variant="contained" startIcon={<FilterAltIcon />}>
                    Filter
                  </Button>
                </Box>
                <FilterDrawer open={openDrawer} setOpen={setDrawer}>
                  <Box sx={{ width: 300, pt: 3 }} role="presentation">
                    <PublicFilter isOpenDrawer={openDrawer} />
                  </Box>
                </FilterDrawer>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                  <ClearButton />
                </Box>
                <Grid container spacing={2} sx={{ mt: 3 }}>
                  {posts.length > 0 ? eachPost : (
                    <Grid item xs={12}>
                      <Alert severity="warning" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No posts found.</Alert>
                    </Grid>
                  )}
                </Grid>
                <AppPagination pageCount={lastPage} />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ backgroundColor: "#1a5b8e", width: '100%' }}>
            <CopyRight />
          </Box>
        </>
      )}
    </Box>
  );
};

export default Jobs;
